/**
 * BurnRitual — the burn animation component used at the end of arc sessions 1 and 2
 * (and optionally session 3 of Letters to Others at the user's choice).
 *
 * Flow:
 *   1. Preview screen — shows the letter text, print option, Burn button
 *   2. Confirmation — "Are you sure? This cannot be undone."
 *   3. Fire animation — CSS flame for ~3 seconds
 *   4. onBurn() callback fires (caller handles permanent delete)
 *
 * The letter text is shown as-is — no editing at this stage.
 * Print is a native window.print() call; no server involvement.
 *
 * Sage requirement: always include somatic containment language before/after burn.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

// ── Icons ─────────────────────────────────────────────────────────────────────

function PrintIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 5V2h8v3M4 11H2V7h12v4h-2M4 9h8v5H4V9z"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  )
}

function FlameIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2C12 2 7 8 7 13a5 5 0 0010 0c0-2-1-4-2-5.5C15 9 14 10.5 14 12a2 2 0 01-4 0c0-2 2-5 2-10z"
        fill="currentColor" opacity="0.8"
      />
      <path
        d="M12 14c0 0-1.5-1-1.5-2.5C10.5 13 12 14 12 14z"
        fill="white" opacity="0.4"
      />
    </svg>
  )
}

// ── Fire animation (pure CSS) ─────────────────────────────────────────────────

function FireAnimation() {
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
      }}
    >
      <style>{`
        @keyframes flicker {
          0%   { transform: scaleY(1)   scaleX(1)    translateY(0);   opacity: 0.9; }
          25%  { transform: scaleY(1.1) scaleX(0.92) translateY(-2px); opacity: 1;   }
          50%  { transform: scaleY(0.95) scaleX(1.06) translateY(1px);  opacity: 0.85; }
          75%  { transform: scaleY(1.08) scaleX(0.95) translateY(-1px); opacity: 0.95; }
          100% { transform: scaleY(1)   scaleX(1)    translateY(0);   opacity: 0.9; }
        }
        @keyframes flickerInner {
          0%   { transform: scaleY(1)   scaleX(1)    translateY(0); }
          30%  { transform: scaleY(1.15) scaleX(0.9) translateY(-3px); }
          60%  { transform: scaleY(0.9)  scaleX(1.1) translateY(2px);  }
          100% { transform: scaleY(1)   scaleX(1)    translateY(0); }
        }
        @keyframes riseSmoke {
          0%   { transform: translateY(0)   scaleX(1); opacity: 0.3; }
          100% { transform: translateY(-40px) scaleX(1.5); opacity: 0; }
        }
      `}</style>

      {/* Smoke wisps */}
      {[0, 1, 2].map(i => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: 4,
            height: 20,
            borderRadius: 4,
            background: 'rgba(120,80,40,0.15)',
            marginTop: -60,
            marginLeft: (i - 1) * 14,
            animation: `riseSmoke ${1.4 + i * 0.3}s ease-out ${i * 0.2}s infinite`,
          }}
        />
      ))}

      {/* Outer flame */}
      <div
        style={{
          width: 64,
          height: 80,
          borderRadius: '50% 50% 30% 30% / 60% 60% 40% 40%',
          background: 'radial-gradient(ellipse at 50% 80%, #FF6B35 0%, #FF4500 40%, #CC2200 80%, transparent 100%)',
          animation: 'flicker 0.9s ease-in-out infinite',
          filter: 'blur(1px)',
          position: 'relative',
        }}
      >
        {/* Inner flame */}
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 32,
            height: 52,
            borderRadius: '50% 50% 30% 30% / 60% 60% 40% 40%',
            background: 'radial-gradient(ellipse at 50% 80%, #FFE066 0%, #FFAA00 50%, transparent 100%)',
            animation: 'flickerInner 0.7s ease-in-out infinite',
          }}
        />
        {/* Core */}
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 14,
            height: 28,
            borderRadius: '50% 50% 30% 30% / 60% 60% 40% 40%',
            background: 'radial-gradient(ellipse at 50% 80%, #FFFDE0 0%, #FFE88A 60%, transparent 100%)',
          }}
        />
      </div>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

interface BurnRitualProps {
  /** The letter text to be burned */
  text: string
  /** Called after the fire animation completes — caller handles permanent delete */
  onBurn: () => void
  /** Called if the user cancels before confirming */
  onCancel: () => void
  /** Optional: somatic closing line shown after the burn (Sage requirement) */
  closingLine?: string
}

type Stage = 'preview' | 'confirm' | 'burning' | 'done'

export default function BurnRitual({ text, onBurn, onCancel, closingLine }: BurnRitualProps) {
  const { t } = useTranslation()
  const [stage, setStage] = useState<Stage>('preview')

  // Auto-advance from 'burning' to 'done' after the animation
  useEffect(() => {
    if (stage !== 'burning') return
    const timer = setTimeout(() => {
      setStage('done')
      onBurn()
    }, 3200)
    return () => clearTimeout(timer)
  }, [stage, onBurn])

  const handlePrint = () => {
    // Temporarily render letter in a print-only container
    const printWindow = window.open('', '_blank')
    if (!printWindow) return
    // The print dialog and the printed page both show this title, so it is
    // user-facing copy and belongs in the catalogue like everything else (#35).
    printWindow.document.write(`
      <html><head><title>${t('arc.letterPrintTitle')}</title>
      <style>
        body { font-family: Georgia, serif; font-size: 15px; line-height: 1.8;
               max-width: 580px; margin: 60px auto; color: #1C1128; }
        p { white-space: pre-wrap; }
      </style></head>
      <body><p>${text.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p></body></html>
    `)
    printWindow.document.close()
    printWindow.focus()
    printWindow.print()
    printWindow.close()
  }

  // ── Preview ────────────────────────────────────────────────────────────────
  if (stage === 'preview') {
    return (
      <div
        className="min-h-svh flex flex-col"
        style={{ background: '#1C1128' }}
      >
        <div className="flex-1 overflow-y-auto px-6 pt-10 pb-4 max-w-xl mx-auto w-full">
          {/* Letter text */}
          <div
            className="rounded-2xl p-6 mb-8"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <p
              className="text-sm leading-relaxed whitespace-pre-wrap"
              style={{ color: 'rgba(255,255,255,0.75)', fontFamily: 'Georgia, serif' }}
            >
              {text}
            </p>
          </div>

          {/* Containment text (Sage requirement: somatic grounding before burn) */}
          <p
            className="text-xs text-center leading-relaxed mb-8"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            {t('arc.previewGrounding')}
            <br />
            {t('arc.previewWarning')}
          </p>
        </div>

        {/* Footer actions */}
        <div className="px-6 pb-10 max-w-xl mx-auto w-full flex flex-col gap-3">
          <button
            onClick={handlePrint}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl text-sm transition-opacity hover:opacity-70"
            style={{
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.5)',
            }}
          >
            <PrintIcon />
            {t('arc.printBeforeBurning')}
          </button>

          <button
            onClick={() => setStage('confirm')}
            className="flex items-center justify-center gap-2 w-full py-4 rounded-xl text-sm font-medium transition-opacity hover:opacity-90"
            style={{ background: '#FF4500', color: '#fff' }}
          >
            <FlameIcon />
            {t('arc.burnLetter')}
          </button>

          <button
            onClick={onCancel}
            className="w-full py-3 text-sm transition-opacity hover:opacity-70"
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}
          >
            {t('arc.notYet')}
          </button>
        </div>
      </div>
    )
  }

  // ── Confirmation ───────────────────────────────────────────────────────────
  if (stage === 'confirm') {
    return (
      <div
        className="min-h-svh flex flex-col items-center justify-center px-6"
        style={{ background: '#1C1128' }}
      >
        <div className="max-w-sm w-full text-center">
          <p
            className="text-xl mb-3"
            style={{ color: 'rgba(255,255,255,0.9)', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
          >
            {t('arc.confirmHeading')}
          </p>
          <p
            className="text-sm leading-relaxed mb-10"
            style={{ color: 'rgba(255,255,255,0.45)' }}
          >
            {t('arc.confirmBody')}
            <br />
            {t('arc.confirmPoint')}
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => setStage('burning')}
              className="w-full py-4 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-opacity hover:opacity-90"
              style={{ background: '#FF4500', color: '#fff' }}
            >
              <FlameIcon />
              {t('arc.confirmYes')}
            </button>
            <button
              onClick={() => setStage('preview')}
              className="w-full py-3 text-sm transition-opacity hover:opacity-70"
              style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }}
            >
              {t('arc.confirmBack')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ── Burning animation ──────────────────────────────────────────────────────
  if (stage === 'burning') {
    return (
      <div
        className="min-h-svh flex flex-col items-center justify-center px-6"
        style={{
          background: 'radial-gradient(ellipse at 50% 70%, rgba(255,80,0,0.15) 0%, #1C1128 60%)',
        }}
      >
        <div style={{ position: 'relative', height: 120, display: 'flex', alignItems: 'flex-end' }}>
          <FireAnimation />
        </div>
        <p
          className="text-sm mt-8 text-center"
          style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
        >
          {t('arc.burningLine')}
        </p>
      </div>
    )
  }

  // ── Done ───────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-svh flex flex-col items-center justify-center px-6 text-center"
      style={{ background: '#1C1128' }}
    >
      <div className="max-w-sm w-full">
        <p
          className="text-2xl mb-4"
          style={{ color: 'rgba(255,255,255,0.85)', fontFamily: 'Georgia, serif', fontStyle: 'italic' }}
        >
          {t('arc.doneHeading')}
        </p>
        <p
          className="text-sm leading-relaxed"
          style={{ color: 'rgba(255,255,255,0.45)' }}
        >
          {closingLine ?? t('arc.doneFallback')}
        </p>
      </div>
    </div>
  )
}
